import axios from "axios";

const CONFIGURED_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const hostname = typeof window !== "undefined" ? window.location.hostname : "";
const isLocalWeb = hostname === "localhost" || hostname === "127.0.0.1";
const isTauri = typeof window !== "undefined" && (
  "__TAURI_INTERNALS__" in window ||
  window.location.protocol === "tauri:" ||
  hostname === "tauri.localhost"
);

// Production web uses the frontend's same-origin /api proxy. This removes
// browser CORS preflight round-trips on every new page. Local development and
// the Tauri desktop build keep using the configured public backend directly.
const BACKEND_URL = (isLocalWeb || isTauri) ? CONFIGURED_BACKEND_URL : "";
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, timeout: 30000 });

let authToken = null;
let onUnauthorized = null;
const getCache = new Map();
let cacheGeneration = 0;
const GET_CACHE_TTL_MS = 5000;

function workspaceKey() {
  if (typeof sessionStorage === "undefined") return "";
  const company = sessionStorage.getItem("sgp_company_id") || "";
  const year = sessionStorage.getItem("sgp_financial_year") || "";
  return `${company}::${year}`;
}

function cacheKey(config) {
  const params = config.params ? JSON.stringify(config.params) : "";
  return `${workspaceKey()}::${config.url || ""}::${params}`;
}

function isCacheableGet(config) {
  if ((config.method || "get").toLowerCase() !== "get") return false;
  const url = config.url || "";
  if (url.startsWith("/auth/") || url.startsWith("/admin/change-log")) return false;
  if (config.responseType === "blob") return false;
  return true;
}

export function clearApiCache() {
  getCache.clear();
  cacheGeneration += 1;
}

export function setAuthToken(token) {
  if (authToken !== token) clearApiCache();
  authToken = token;
}

export function setUnauthorizedHandler(fn) {
  onUnauthorized = fn;
}

api.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (method !== "get") clearApiCache();

  if (isCacheableGet(config)) {
    const key = cacheKey(config);
    // Keep the dispatch context: the selected company/year or identity may
    // change while this request is in flight.
    config.__sgpCacheKey = key;
    config.__sgpCacheGeneration = cacheGeneration;
    const cached = getCache.get(key);
    if (cached && Date.now() - cached.at < GET_CACHE_TTL_MS) {
      config.__sgpCacheHit = true;
      config.adapter = async () => ({
        data: cached.data,
        status: 200,
        statusText: "OK",
        headers: cached.headers || {},
        config,
        request: null,
      });
    }
  }

  if (authToken && !config.url?.startsWith("/auth/login") && !config.url?.startsWith("/auth/accept-invite")) {
    config.headers.Authorization = `Bearer ${authToken}`;
    const company = sessionStorage.getItem("sgp_company_id");
    const financialYear = sessionStorage.getItem("sgp_financial_year");
    if (company && financialYear && !config.url?.startsWith("/workspaces")) {
      config.headers["X-SGP-Company-ID"] = company;
      config.headers["X-SGP-Financial-Year"] = financialYear;
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => {
    if (isCacheableGet(response.config) && !response.config.__sgpCacheHit
      && response.config.__sgpCacheGeneration === cacheGeneration
      && response.config.__sgpCacheKey === cacheKey(response.config)) {
      getCache.set(response.config.__sgpCacheKey, {
        at: Date.now(),
        data: response.data,
        headers: response.headers,
      });
    }
    return response;
  },
  async (err) => {
    if (err.response?.status === 401 && onUnauthorized) {
      onUnauthorized();
    }
    let details = err.response?.data;
    if (details instanceof Blob && details.size < 65536) {
      try { details = JSON.parse(await details.text()); } catch { /* Keep the normal network message. */ }
    }
    const msg = details?.detail || err.message || "Request failed";
    return Promise.reject(new Error(typeof msg === "string" ? msg : JSON.stringify(msg)));
  }
);

export default api;

